/**
 * SDR Stock Manager - Configuration & System Constants
 * @module config/constants
 */

const envUrl = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SUPABASE_URL;
const envAnonKey = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SUPABASE_ANON_KEY;
const envBucket = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SUPABASE_STORAGE_BUCKET;

/**
 * Embedded Supabase cloud configuration defaults with Vite env support.
 */
export const SUPABASE_CONFIG = Object.freeze({
  DEFAULT_URL: envUrl || 'https://lrdiokqvxsprmnzeajaw.supabase.co',
  DEFAULT_ANON_KEY: envAnonKey || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxyZGlva3F2eHNwcm1uemVhamF3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODA4NDg4MTAsImV4cCI6MjA5NjQyNDgxMH0.quhetID1PFj4HYCEFxRNRxD5S5gPO5VjZKDVnNofYyY',
  STORAGE_BUCKET: envBucket || 'item-images',
  REALTIME_CHANNEL: 'sdr-inventory-db-changes'
});

/**
 * Standard authorized domains allowed to authenticate into the system.
 */
export const ALLOWED_AUTH_DOMAINS = Object.freeze([
  'student.mahidol.edu',
  'student.mahidol.ac.th',
  'mahidol.edu',
  'mahidol.ac.th'
]);

/**
 * PostgreSQL / PostgREST table name mapping.
 */
export const TABLES = Object.freeze({
  INVENTORY: 'inventory',
  TRANSACTIONS: 'transactions',
  APP_SETTINGS: 'app_settings',
  STOCK_AUDITS: 'stock_audits',
  STOCK_OUT_MASTER: 'stock_out_master',
  STOCK_OUT_DETAILS: 'stock_out_details',
  ACCESS_CONTROL: 'access_control',
  USER_WHITELIST: 'user_whitelist'
});

/**
 * LocalStorage cache keys.
 */
export const STORAGE_KEYS = Object.freeze({
  SESSION: 'sdr_auth_session',
  USER: 'sdr_auth_user',
  SETTINGS: 'sdr_settings',
  INVENTORY: 'sdr_inventory',
  TRANSACTIONS: 'sdr_transactions',
  STOCK_AUDITS: 'sdr_stock_audits',
  STOCK_OUT_MASTER: 'sdr_stock_out_master',
  STOCK_OUT_DETAILS: 'sdr_stock_out_details',
  ACTIVE_AUDIT_ID: 'sdr_active_audit_id',
  BACKUPS: 'sdr_backups',
  LAST_SYNC_AT: 'sdr_last_cloud_sync_at',
  LEGACY_SHEET_URL: 'sdr_sheet_url',
  LEGACY_SUPABASE_URL: 'sdr_supabase_url',
  LEGACY_SUPABASE_ANON_KEY: 'sdr_supabase_anon_key',
  LEGACY_R2_UPLOAD_URL: 'sdr_r2_upload_url',
  LEGACY_LOW_STOCK_THRESHOLD: 'sdr_low_stock_threshold',
  LEGACY_EXPIRY_WARNING_DATE: 'sdr_expiry_warning_date',
  LEGACY_STORAGE_LOCATIONS: 'sdr_storage_locations'
});

/**
 * Default application settings.
 */
export const DEFAULT_SETTINGS = Object.freeze({
  sheetUrl: '',
  supabaseUrl: envUrl || 'https://lrdiokqvxsprmnzeajaw.supabase.co',
  supabaseAnonKey: envAnonKey || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxyZGlva3F2eHNwcm1uemVhamF3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODA4NDg4MTAsImV4cCI6MjA5NjQyNDgxMH0.quhetID1PFj4HYCEFxRNRxD5S5gPO5VjZKDVnNofYyY',
  r2UploadUrl: envBucket || 'item-images',
  lowStockThreshold: 5,
  expiryWarningDate: '',
  storageLocations: 'ตู้ A, ตู้ B, ตู้ C, ชั้นวาง 1, ชั้นวาง 2, โต๊ะทำงาน, ลังสำรอง'
});

/**
 * Image processing & compression configuration limits.
 */
export const IMAGE_CONFIG = Object.freeze({
  MAX_UPLOAD_BYTES: 1024 * 1024, // 1 MB
  MAX_DIMENSION: 1600, // 1600px max width or height
  INITIAL_QUALITY: 0.85,
  MIN_QUALITY: 0.45,
  MAX_COMPRESSION_ATTEMPTS: 8,
  SCALE_FACTOR: 0.82,
  QUALITY_STEP: 0.06
});

/**
 * Cloud synchronization retry and debounce configuration.
 */
export const SYNC_CONFIG = Object.freeze({
  RETRY_DELAYS_MS: Object.freeze([5000, 15000, 30000, 60000, 120000]),
  CHUNK_SIZE: 50,
  REALTIME_DEBOUNCE_MS: 650,
  SELF_ECHO_PAUSE_MS: 1500,
  FILTER_DEBOUNCE_MS: 180
});
